import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layouts/AppLayout';
import useAuth from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import './FeatureConfig.css';

const FeatureConfig = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { features, loading, updateFeature, refetch } = useFeatures();

  const [updates, setUpdates] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const parseCargos = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    try {
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  // ✅ Proteger página: apenas admin
  useEffect(() => {
    if (!authLoading && user?.cargo !== 'admin') {
      navigate('/home');
    }
  }, [authLoading, user, navigate]);

  // ✅ Inicializar estado com features atuais (apenas quando features carregarem)
  useEffect(() => {
    if (loading || !features.length) return;
    
    const initialUpdates = {};
    features.forEach(feature => {
      initialUpdates[feature.nome] = {
        ativo: feature.ativo === 1,
        visivel_para_cargo: parseCargos(feature.visivel_para_cargo)
      };
    });
    setUpdates(initialUpdates);
  }, [features, loading]);

  // ✅ Handle toggle de feature (ativo/inativo)
  const handleToggle = (featureName) => {
    setUpdates(prev => ({
      ...prev,
      [featureName]: {
        ...prev[featureName],
        ativo: !prev[featureName]?.ativo
      }
    }));
  };

  // ✅ Handle mudança de visibilidade por cargo
  const handleCargoToggle = (featureName, cargo) => {
    setUpdates(prev => {
      const currentCargos = prev[featureName]?.visivel_para_cargo || [];
      const newCargos = currentCargos.includes(cargo)
        ? currentCargos.filter(c => c !== cargo)
        : [...currentCargos, cargo];
      
      return {
        ...prev,
        [featureName]: {
          ...prev[featureName],
          visivel_para_cargo: newCargos
        }
      };
    });
  };

  // ✅ Salvar alterações
  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Atualizar cada feature que foi modificada
      const promises = features.map(async (feature) => {
        const update = updates[feature.nome];
        if (!update) return;

        const originalAtivo = feature.ativo === 1;
        const originalCargos = parseCargos(feature.visivel_para_cargo);

        const changed = 
          update.ativo !== originalAtivo || 
          JSON.stringify(update.visivel_para_cargo.sort()) !== JSON.stringify(originalCargos.sort());

        if (changed) {
          await updateFeature(feature.nome, {
            ativo: update.ativo,
            visivel_para_cargo: update.visivel_para_cargo
          });
        }
      });

      await Promise.all(promises);

      setMessage({ type: 'success', text: '✅ Features atualizadas com sucesso!' });
      
      // Refetch local (admin view)
      await refetch();

      // Limpar mensagem após 3 segundos
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao salvar features'
      });
    } finally {
      setSaving(false);
    }
  };

  // ✅ Se ainda está carregando auth, mostrar loading
  if (authLoading || loading) {
    return (
      <AppLayout
        titulo="Gerenciar Features"
        cargo={user?.cargo}
        problemas={[]}
      >
        <div className="feature-config-content">
          <div className="loading-spinner">Carregando...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      titulo="Gerenciar Features"
      cargo={user?.cargo}
      problemas={[]}
    >
      <div className="feature-config-content">
        {/* ✅ Header */}
        <div className="feature-config-header">
          <h1>🚀 Gerenciar Features</h1>
          <p>Controle quais funcionalidades estão disponíveis neste condomínio</p>
        </div>

        {/* ✅ Mensagem de status */}
        {message.text && (
          <div className={`feature-config-message feature-config-message-${message.type}`}>
            {message.text}
          </div>
        )}

        {/* ✅ Grid de features */}
        {features.length === 0 ? (
          <div className="feature-empty">
            Nenhuma feature encontrada para este condomínio.
          </div>
        ) : (
          <div className="feature-config-grid">
            {features.map((feature) => (
              <div key={feature.nome} className="feature-item">
                <div className="feature-item-header">
                  <h3>{feature.titulo}</h3>
                  <div className={`feature-toggle ${updates[feature.nome]?.ativo ? 'active' : 'inactive'}`}>
                    <input
                      type="checkbox"
                      id={`feature-${feature.nome}`}
                      checked={updates[feature.nome]?.ativo || false}
                      onChange={() => handleToggle(feature.nome)}
                      disabled={saving}
                    />
                    <label htmlFor={`feature-${feature.nome}`}>
                      <span className="toggle-switch"></span>
                    </label>
                  </div>
                </div>

                <p className="feature-item-description">{feature.descricao}</p>

                {/* ✅ Checkboxes de visibilidade por cargo */}
                <div className="feature-cargo-visibility">
                  <label className="cargo-label">Visível para:</label>
                  <div className="cargo-checkboxes">
                    {['admin', 'sindico', 'portaria'].map(cargo => (
                      <label key={cargo} className="cargo-checkbox">
                        <input
                          type="checkbox"
                          checked={updates[feature.nome]?.visivel_para_cargo?.includes(cargo) || false}
                          onChange={() => handleCargoToggle(feature.nome, cargo)}
                          disabled={saving}
                        />
                        {cargo}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="feature-item-meta">
                  <span className="feature-category">
                    {feature.categoria === 'dispositivos' && '🎛️'}
                    {feature.categoria === 'configuracao' && '⚙️'}
                    {feature.categoria}
                  </span>
                  <span className={`feature-status ${updates[feature.nome]?.ativo ? 'status-ativo' : 'status-inativo'}`}>
                    {updates[feature.nome]?.ativo ? '✓ Ativo' : '✗ Inativo'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ✅ Botões de ação */}
        <div className="feature-config-actions">
          <button
            className="btn-cancel"
            onClick={() => navigate('/configuracoes')}
            disabled={saving}
          >
            Voltar
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </AppLayout>
  );
};

export default FeatureConfig;
